import {
  BoundForm,
  boundRadioGroupField,
  boundSelectCardGroupField,
  Button,
  Css,
  ModalBody,
  ModalFooter,
  ModalHeader,
  useComputed,
  useModal,
} from "@homebound/beam";
import { useFormState } from "@homebound/form-state";
import { Observer } from "mobx-react";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { ChangeEventReasonsBoundSelectField } from "src/components/autoPopulateSelects/ChangeEventReasonsBoundSelectField";
import { ChangeEventType, ChangeEventWorkflow, ChangeEventWorkflowDetail } from "src/generated/graphqlTypes";
import {
  CreateGuidedChangeEventModal_ChangeEventFragment,
  CreateGuidedChangeEventModalQueryResult,
  useCreateGuidedChangeEventModal_ChangeEventQuery,
  useCreateGuidedChangeEventModalQuery,
} from "src/routes/projects/change-events/components/CreateGuidedChangeEventModal.generated";
import { guidedWorkflows } from "src/routes/projects/change-events/guided/workflows/registry";
import { defaultChangeEventTypeByLotType } from "src/routes/projects/components/utils";
import { createAddGuidedChangeEventUrl, createGuidedChangeEventUrl } from "src/RouteUrls";
import { queriesResult } from "src/utils";
import { baseChangeEventFormConfig } from "../guided/baseChangeEventForm";
import { useGuidedChangeEventActions } from "../guided/workflows/sharedSteps/useGuidedChangeEventActions";

type CreateGuidedChangeEventModalProps = {
  projectId: string;
  /** Provided when editing a draft */
  changeEventId?: string;
};

export function CreateGuidedChangeEventModal(props: CreateGuidedChangeEventModalProps) {
  const { projectId, changeEventId } = props;
  const query = useCreateGuidedChangeEventModalQuery({ variables: { projectId } });
  const changeEventQuery = useCreateGuidedChangeEventModal_ChangeEventQuery({
    variables: { changeEventId: changeEventId! },
    skip: !changeEventId,
  });

  return queriesResult([query, changeEventQuery] as const, {
    data: (data, changeEventData) => (
      <CreateGuidedChangeEventModalView
        data={data}
        projectId={projectId}
        existingChangeEvent={changeEventData?.changeEvent}
      />
    ),
  });
}

type CreateGuidedChangeEventModalViewProps = {
  projectId: string;
  existingChangeEvent?: CreateGuidedChangeEventModal_ChangeEventFragment;
  data: Exclude<CreateGuidedChangeEventModalQueryResult["data"], undefined>;
};

function CreateGuidedChangeEventModalView(props: CreateGuidedChangeEventModalViewProps) {
  const { data, projectId, existingChangeEvent } = props;
  const { project, enumDetails } = data;
  const workflows = enumDetails.changeEventWorkflow;
  const { lotType, shouldAccrueAsComplete } = project;
  const { clientNoun } = lotType;

  const isEdit = !!existingChangeEvent;

  const formState = useFormState({
    config: baseChangeEventFormConfig,
    init: {
      input: existingChangeEvent,
      map: (input) => ({
        id: input.id,
        reasonId: input.reason.id,
        workflow: input.workflow?.code,
        projectStageId: input.projectStage.id,
        type: isAlwaysChargeable(input.workflow?.code) ? ChangeEventType.External : input.type.code,
      }),
      ifUndefined: {
        projectStageId: project.stage!.id,
        type: defaultChangeEventTypeByLotType({ lotType: lotType.code, shouldAccrueAsComplete }),
      },
    },
  });
  const { saveChangeEvent } = useGuidedChangeEventActions({ form: formState, projectId });
  const { closeModal, openModal } = useModal();
  const navigate = useNavigate();

  const workflowOptions = useMemo(() => toWorkflowOptions(workflows), [workflows]);
  const selectedWorkflow = useComputed(() => formState.workflow.value, [formState]);
  const forceChargeable = isAlwaysChargeable(selectedWorkflow);

  return (
    <>
      <ModalHeader>Describe Change</ModalHeader>
      <ModalBody>
        <p css={Css.sm.gray700.mb3.$}>Tell us what type of change this is.</p>
        <BoundForm
          formState={formState}
          rows={[
            {
              workflow: boundSelectCardGroupField({
                view: "list",
                labelStyle: "hidden",
                options: workflowOptions,
                onChange: (value) => {
                  // Reset the reason id when the workflow changes
                  formState.reasonId.set(undefined);
                  formState.workflow.set(value);
                  if (isAlwaysChargeable(value)) formState.type.set(ChangeEventType.External);
                },
              }),
            },
            {
              reactNodeReason: (
                <ChangeEventReasonsBoundSelectField
                  field={formState.reasonId}
                  label="Reason"
                  workflow={selectedWorkflow ?? undefined}
                  disabled={!selectedWorkflow}
                />
              ),
            },
            {
              type: boundRadioGroupField({
                label: `Chargeable to ${clientNoun}`,
                layout: "horizontal",
                options: [
                  {
                    value: ChangeEventType.Internal,
                    label: "No",
                    disabled:
                      forceChargeable && `This type of change is always priced and billed to the ${clientNoun}.`,
                  },
                  {
                    value: ChangeEventType.External,
                    label: "Yes",
                    // Certain projects have an agreement where every cost after completion is HTI's cost,
                    // so they can't create chargeable change events post-completion.
                    disabled:
                      shouldAccrueAsComplete &&
                      `This project has reached investor closeout. Changes cannot be made to the ${clientNoun} price.`,
                  },
                ],
                helperText: `Determines whether this change is priced and billed to the ${clientNoun}. 'Yes' will capture a ${clientNoun}
                  price alongside cost, and route for signature before it's final. 'No' will be treated as an internal
                  change. No ${clientNoun} price is captured, and no signature is required.`,
              }),
            },
          ]}
        />
      </ModalBody>
      <ModalFooter>
        <Observer>
          {() => (
            <>
              <Button label="Cancel" onClick={closeModal} variant="quaternary" />
              <Button
                disabled={!formState.valid}
                label={isEdit ? "Save" : "Start Change Event"}
                onClick={async () => {
                  if (!formState.canSave()) return;
                  if (isEdit) {
                    await saveChangeEvent();
                    closeModal();
                    return navigate(createGuidedChangeEventUrl(projectId, existingChangeEvent.id), {
                      state: formState.value,
                    });
                  }
                  const input = formState.value;
                  const enterWorkflow = () => {
                    closeModal();
                    navigate(createAddGuidedChangeEventUrl(projectId), { state: input });
                  };
                  // A workflow can interpose a modal before its wizard starts, i.e. to warn about a destructive flow.
                  const gate = guidedWorkflows[input.workflow!]?.gate;
                  if (!gate) {
                    return enterWorkflow();
                  } else {
                    openModal({
                      content: gate({
                        projectId,
                        input,
                        onContinue: enterWorkflow,
                        onChooseAnotherWorkflow: () => {
                          openModal({ content: <CreateGuidedChangeEventModal projectId={projectId} /> });
                        },
                      }),
                    });
                  }
                }}
              />
            </>
          )}
        </Observer>
      </ModalFooter>
    </>
  );
}

/** These workflows always produce a change that is priced and billed to the client. */
function isAlwaysChargeable(workflow: ChangeEventWorkflow | null | undefined) {
  return workflow === ChangeEventWorkflow.ApplyInitialConfiguration;
}

/**
 * The enum lists every workflow the business plans to support, but only the ones in `guidedWorkflows`
 * have a wizard to run, so the rest are shown-but-disabled instead of creating a dead-end draft.
 */
function toWorkflowOptions(workflows: Pick<ChangeEventWorkflowDetail, "code" | "name" | "description">[]) {
  return workflows.map((w) => {
    const isBuilt = !!guidedWorkflows[w.code];
    return {
      value: w.code,
      label: w.name,
      description: w.description,
      disabled: !isBuilt,
      tooltip: isBuilt ? undefined : "Coming soon.",
    };
  });
}
